/**
 * Extracts string value from panchanga astrological fields
 * Backend returns objects like {name: "शुक्ल", nameEnglish: "Shukla", number: 1}
 * This function safely extracts the display string
 */
export function extractPanchangaValue(value: any): string {
  if (!value) {
    return '';
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  // Handle object format from backend
  if (typeof value === 'object') {
    // Backend returns {name, nameEnglish, number}
    if ('name' in value && typeof value.name === 'string') {
      return value.name;
    }
    if ('nameNepali' in value && typeof value.nameNepali === 'string') {
      return value.nameNepali;
    }
    if ('nameEnglish' in value && typeof value.nameEnglish === 'string') {
      return value.nameEnglish;
    }
  }
  
  // Last resort: convert to string (but this should rarely happen)
  return String(value || '');
}

/**
 * Return language-aware label for panchanga astrological values.
 * If `lang` is 'english' and backend object contains `nameEnglish` use it,
 * otherwise fall back to Nepali `name`.
 */
// Karana names (each tithi has two karanas; "vishti" is also called "bhadra").
// Shared across the string and object branches of extractPanchangaValueIntl
// so an object payload with no nameNepali field still gets translated instead
// of silently falling back to the English name.
const KARANA_TRANSLIT_TO_NEPALI: Record<string, string> = {
  bava: 'बव',
  balava: 'बालव',
  kaulava: 'कौलव',
  taitila: 'तैतिल',
  taitula: 'तैतिल',
  garaja: 'गर',
  garija: 'गर',
  vanija: 'वणिज',
  vishti: 'विष्टि (भद्रा)',
  bhadra: 'विष्टि (भद्रा)',
  'vishti(bhadra)': 'विष्टि (भद्रा)',
  'bhadra(vishti)': 'विष्टि (भद्रा)',
  shakuni: 'शकुनि',
  chatushpada: 'चतुष्पाद',
  naga: 'नाग',
  kimstughna: 'किंस्तुघ्न'
};

function translitNameToNepali(name: string): string | null {
  const key = name.trim().toLowerCase();
  if (KARANA_TRANSLIT_TO_NEPALI[key]) return KARANA_TRANSLIT_TO_NEPALI[key];
  const compoundMatch = key.match(/^([a-z]+)\s*\(([a-z]+)\)$/);
  if (compoundMatch) {
    const [, first, second] = compoundMatch;
    if (KARANA_TRANSLIT_TO_NEPALI[first]) return KARANA_TRANSLIT_TO_NEPALI[first];
    if (KARANA_TRANSLIT_TO_NEPALI[second]) return KARANA_TRANSLIT_TO_NEPALI[second];
  }
  return null;
}

export function extractPanchangaValueIntl(value: any, lang: 'nepali' | 'english' = 'nepali'): string {
  if (!value) return '';
  // value can be either an object {name: 'नेपाली', nameEnglish: 'English'} or a string
  if (typeof value === 'string') {
    const raw = value.trim();
    const key = raw.toLowerCase();

    // Common mapping tables
    const rashiToEnglish: Record<string, string> = {
      mesha: 'Mesha',
      aries: 'Mesha',
      vrisha: 'Vrishabha',
      vrishabha: 'Vrishabha',
      vrishab: 'Vrishabha',
      mithuna: 'Mithuna',
      mithun: 'Mithuna',
      karkata: 'Karka',
      karka: 'Karka',
      simha: 'Simha',
      kanya: 'Kanya',
      tula: 'Tula',
      vrischika: 'Vrishchika',
      vrishchika: 'Vrishchika',
      dhanu: 'Dhanu',
      makara: 'Makara',
      kumbha: 'Kumbha',
      meena: 'Meena',
      pisces: 'Meena'
    };

    const thitiNakshatraKaranNepaliToEnglish: Record<string, string> = {
      'शुक्ल': 'Shukla',
      'कृष्ण': 'Krishna',
      'बुद्ध': 'Budha',
      // Nakshatra / Karana examples (add more as needed)
      'रोहिणी': 'Rohini',
      'कृत्तिका': 'Krittika',
      'अश्लेषा': 'Ashlesha',
    };

    if (lang === 'english') {
      // Try parsing JSON-encoded object first
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          return parsed.nameEnglish || parsed.name || parsed.nameNepali || raw;
        }
      } catch (e) {
        // not JSON, continue
      }

      // Map known Nepali text to English
      if (thitiNakshatraKaranNepaliToEnglish[key]) return thitiNakshatraKaranNepaliToEnglish[key];

      // Map transliterated/short keys (e.g., 'mesha') to English
      if (rashiToEnglish[key]) return rashiToEnglish[key];

      // If looks like Nepali script, attempt to return as-is (fallback)
      return raw;
    }

    // Nepali mode: try to parse JSON object or map transliterations to Nepali names
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (parsed.nameNepali) return parsed.nameNepali;
        const translated = translitNameToNepali(parsed.name || parsed.nameEnglish || '');
        return translated || parsed.name || parsed.nameEnglish || raw;
      }
    } catch (e) {
      // not JSON
    }

    // Map common transliterations to Nepali where appropriate
    const translitToNepali: Record<string, string> = {
      mesha: 'मेष',
      aries: 'मेष',
      vrisha: 'वृष',
      vrishabha: 'वृषभ',
      mithuna: 'मिथुन',
      karka: 'कर्क',
      karkata: 'कर्कट',
      simha: 'सिंह',
      kanya: 'कन्या',
      tula: 'तुला',
      vrischika: 'वृश्चिक',
      dhanu: 'धनु',
      makara: 'मकर',
      kumbha: 'कुम्भ',
      meena: 'मीन',
      // Karana names (each tithi has two karanas; "vishti" is also called "bhadra")
      bava: 'बव',
      balava: 'बालव',
      kaulava: 'कौलव',
      taitila: 'तैतिल',
      taitula: 'तैतिल',
      garaja: 'गर',
      garija: 'गर',
      vanija: 'वणिज',
      vishti: 'विष्टि (भद्रा)',
      bhadra: 'विष्टि (भद्रा)',
      'vishti(bhadra)': 'विष्टि (भद्रा)',
      'bhadra(vishti)': 'विष्टि (भद्रा)',
      shakuni: 'शकुनि',
      chatushpada: 'चतुष्पाद',
      naga: 'नाग',
      kimstughna: 'किंस्तुघ्न'
    };

    if (translitToNepali[key]) return translitToNepali[key];

    // Compound names like "Vishti(Bhadra)" or "Bhadra (Vishti)" — translate
    // the primary name before the parenthesis if the exact compound wasn't matched.
    const compoundMatch = key.match(/^([a-z]+)\s*\(([a-z]+)\)$/);
    if (compoundMatch) {
      const [, first, second] = compoundMatch;
      if (translitToNepali[first]) return translitToNepali[first];
      if (translitToNepali[second]) return translitToNepali[second];
    }

    return raw;
  }
  if (lang === 'english') {
    return value.nameEnglish || value.name || value.nameNepali || '';
  }
  // default: nepali display
  if (value.nameNepali) return value.nameNepali;
  const translated = translitNameToNepali(value.name || value.nameEnglish || '');
  return translated || value.name || value.nameEnglish || '';
}
